import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import 'katex/dist/katex.min.css';
import { BlockMath, InlineMath } from 'react-katex';
import { X } from 'lucide-react';
import { espacerMilliersTex } from '../../../../../../utils/milliers';

/**
 * Expression mathématique côté élève — fluide dans le texte, zoom possible.
 *
 * Taille : celle du texte autour, jamais la sienne (2026-09-12). Avant, le
 * composant imposait `text-sm` : sur la projection à 60 px, une fraction de
 * gen_607 insérée par {{element:0}} restait à 14 px, illisible depuis le
 * fond de la classe. Le zoom garde sa grande taille.
 *
 * Forme : une expression d'UNE ligne reste EN LIGNE dans la phrase (« On sait
 * que 6/7 ≈ 0,8571… »), insécable ; un contenu de plusieurs lignes garde le
 * bloc centré, c'est sa raison d'être.
 */
const ExpressionNumeriqueElementEleve = ({ config, theme = 'dark' }) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const contenu = config?.contenu || '';

  // Bloquer le scroll du body quand le zoom est ouvert
  useEffect(() => {
    if (!isZoomed) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [isZoomed]);

  if (!contenu) return null;

  const lignes = contenu.split('\n').map((l) => l.trim()).filter(Boolean);
  const enLigne = lignes.length === 1;
  const texDe = (ligne) => espacerMilliersTex(ligne.replace(/▢/g, '\\square'));

  // zoom : couleur claire forcée — la modale pose un fond slate-950, le
  // KaTeX hérite sinon de la couleur du thème (noir sur noir en light)
  const renderContent = (scale = '', zoom = false) => (
    <div className={`flex flex-col items-center justify-center ${scale} ${zoom ? 'text-slate-100' : ''}`}>
      {contenu.split('\n').map((line, idx) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={idx} className="h-2" />;

        try {
          // Espaces de milliers à l'affichage (utils/milliers.js).
          const displayText = texDe(trimmed);
          return (
            <div key={idx} className="my-1 [&_.katex]:text-inherit">
              <BlockMath math={displayText} />
            </div>
          );
        } catch {
          return <p key={idx} className={zoom ? 'text-slate-100' : (theme === 'light' ? 'text-gray-900' : 'text-slate-300')}>{trimmed}</p>;
        }
      })}
    </div>
  );

  return (
    <>
      {enLigne ? (
        // Une ligne : dans le flux de la phrase, à la taille du texte, jamais
        // coupée (\displaystyle garde la grande fraction, comme le bloc).
        <span
          className="expr-insecable cursor-zoom-in [&_.katex]:text-inherit"
          onClick={() => setIsZoomed(true)}
        >
          <InlineMath math={`\\displaystyle ${texDe(lignes[0])}`} />
        </span>
      ) : (
        <div
          className="my-1 w-full cursor-zoom-in"
          onClick={() => setIsZoomed(true)}
        >
          {renderContent()}
        </div>
      )}

      {isZoomed && createPortal(
        <div
          className="fixed inset-0 z-[10020] bg-slate-950/95 backdrop-blur-md flex items-center justify-center overflow-hidden"
          onClick={() => setIsZoomed(false)}
        >
          <button
            onClick={() => setIsZoomed(false)}
            className="absolute top-4 right-4 w-10 h-10 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-full flex items-center justify-center text-white transition-colors z-10"
          >
            <X className="w-5 h-5" />
          </button>
          <div
            className="max-w-[95vw] max-h-[90vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {renderContent('text-3xl', true)}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ExpressionNumeriqueElementEleve;
